import { useMemo, useRef } from "react";

import noop from "lodash/noop";

import { ChipDownContainer } from "@similarweb/ui-components";

import type { IChipItem } from "pro-features/components/MultiChipDown/src/MultiChipDownTypes";
import I18n, { useTranslation } from "pro-features/components/WithTranslation/src/I18n";

import { FooterButton } from "../../MultiCategoriesChipDown/src/MultiCategoryChipdownStyles";

import type { IMultiChipDownStatelessProps } from "./MultiChipDownStatelessTypes";

const MultiChipDownStateless = <T extends IChipItem>({
    options = [],
    selectedItems,
    buttonText,
    renderChipTitleText,
    appendTo = null,
    hasSearch = false,
    searchPlaceHolder = "",
    dropdownWidth = 320,
    dropdownPopupHeight = undefined,
    isDisabled = false,
    getDropdownItem,
    virtualize,
    maxVirtualItemsToRender,
    labelFooterButton,
    headerComponent,
    showApplyButton = false,
    cssClassContainer = "DropdownContent-container",
    shouldDisableApplyButton = () => false,
    // @ts-ignore FIXME: strict migration - Type 'null' is not assignable to type 'string' — default returns null; code guards via enabledTooltip={chipTooltipText != null}
    renderChipTooltipText = () => null,
    onToggleDropdown = noop,
    onDone = noop,
    onClickOutside = noop,
    trackingOnApply = noop,
    onItemClick = noop,
    onCloseItem = noop,
    searchFilter,
}: IMultiChipDownStatelessProps<T>) => {
    const t = useTranslation();
    const chipDownRef = useRef(undefined);

    const chipTitleText = useMemo(() => renderChipTitleText(selectedItems), [selectedItems, renderChipTitleText]);
    const chipTooltipText = useMemo(() => renderChipTooltipText(selectedItems), [selectedItems, renderChipTooltipText]);

    const onApply = () => {
        onDone(selectedItems);
        trackingOnApply(selectedItems);
        // @ts-ignore FIXME: strict migration - Property 'closePopup' does not exist on type 'never' — chipDownRef points at the untyped ChipDownContainer instance
        chipDownRef.current?.closePopup();
    };

    const onClick = (selectedItem: IChipItem) => {
        // get the item from the options list to get the full item object
        const item = options.find((item) => item.id === selectedItem.id);
        onItemClick(item, selectedItems);
    };

    const onToggle = (isOpen: boolean, isOutsideClick: boolean) => {
        onToggleDropdown(isOpen);
        if (!isOpen && isOutsideClick) {
            onClickOutside();
        }
    };

    const selectedItemsIds = Object.fromEntries(selectedItems.map((item) => [item.id, true]));

    return (
        <ChipDownContainer
            onCloseItem={onCloseItem}
            ref={chipDownRef}
            tooltipDisabled={selectedItems.length === 1}
            selectedText={chipTitleText}
            tooltipText={chipTooltipText}
            enabledTooltip={chipTooltipText != null}
            closeOnItemClick={false}
            onToggle={onToggle}
            selectedIds={selectedItemsIds}
            width={dropdownWidth}
            dropdownPopupHeight={dropdownPopupHeight}
            buttonText={buttonText}
            noResultsMessage={t("dropdown.no.results")}
            hasSearch={hasSearch}
            searchPlaceHolder={searchPlaceHolder}
            headerComponent={headerComponent}
            // @ts-ignore FIXME: strict migration - searchFilter item type ReactElement vs ChipDownContainer's IDropdownChild — external component type
            searchFilter={searchFilter}
            footerComponent={() =>
                showApplyButton && (
                    <FooterButton
                        onClick={onApply}
                        disabled={shouldDisableApplyButton(selectedItems)}
                    >
                        {labelFooterButton || <I18n>common.apply</I18n>}
                    </FooterButton>
                )
            }
            cssClassContainer={cssClassContainer}
            disabled={isDisabled}
            onClick={onClick}
            virtualize={virtualize}
            maxVirtualItemsToRender={maxVirtualItemsToRender}
            // @ts-ignore FIXME: strict migration - Type 'string | null' is not assignable to 'string | undefined' — external ChipDownContainer appendTo prop
            appendTo={appendTo}
        >
            {options.map(getDropdownItem)}
        </ChipDownContainer>
    );
};

export default MultiChipDownStateless;
